<template>
	<b-modal
		:visible="visible"
		hide-header
		hide-footer
		centered
		size="xl"
		no-close-on-backdrop
		content-class="advertisement-modal"
		@change="onVisibilityChange"
	>
		<div class="advertisement-modal-body">
			<div class="advertisement-modal-header">
				<h1 class="advertisement-modal-title">{{ advertisement ? "Edit" : "Add New" }} Advertisement</h1>
				<button type="button" class="advertisement-modal-close" @click="$emit('close')">
					<img src="@/assets/images/cancel-icon.svg" alt="Close" />
				</button>
			</div>

			<div class="advertisement-modal-columns">
				<!-- Left: form -->
				<div class="advertisement-modal-form">
					<section class="advertisement-section">
						<div class="advertisement-section-title">
							Content
							<small v-if="type === AdvertisementType.CARD" class="advertisement-section-aside">Image required</small>
						</div>

						<div v-if="isAdvertisementHtmlEnabled" class="advertisement-form-group">
							<label class="advertisement-label">Type</label>
							<div class="advertisement-option-cards">
								<div v-for="option in typeOptions" :key="option.value" class="advertisement-option-card-container">
									<input :id="`advertisement-type-${option.value}`" v-model="type" type="radio" name="advertisement-type" :value="option.value" />
									<label class="advertisement-option-card" :for="`advertisement-type-${option.value}`">
										<p>{{ option.title }}</p>
										{{ option.description }}
									</label>
								</div>
							</div>
						</div>

						<div v-if="type === AdvertisementType.CARD" class="advertisement-form-group">
							<div v-if="image" class="advertisement-image-preview">
								<img :src="image" alt="" />
								<button type="button" class="advertisement-image-remove" title="Remove image" @click="image = ''">
									<img src="@/assets/images/cancel-icon.svg" alt="Remove" />
								</button>
							</div>
							<vue-dropzone
								v-else
								ref="dropzone"
								id="advertisement-dropzone"
								class="advertisement-dropzone"
								:options="dropzoneOptions"
								:useCustomSlot="true"
								@vdropzone-file-added="uploadImage"
							>
								<p class="advertisement-dropzone-title">{{ isUploading ? "Uploading..." : "Drag and drop an image" }}</p>
								<p class="advertisement-dropzone-subtitle">or click to select a file.</p>
							</vue-dropzone>
							<small class="d-block text-muted mt-1">Recommended size: 1200 × 600 px</small>
							<small v-if="imageError || visibleErrors.image" class="d-block text-danger mt-1">{{ imageError || visibleErrors.image }}</small>
						</div>
						<div v-else class="advertisement-form-group">
							<label class="advertisement-label">HTML</label>
							<textarea v-model="html" rows="10" class="form-textarea" placeholder="<div>...</div>"></textarea>
							<small v-if="visibleErrors.html" class="d-block text-danger mt-1">{{ visibleErrors.html }}</small>
							<small class="d-block text-muted mt-1">Do not put links inside. Set the link below and the whole card becomes clickable.</small>
						</div>

						<div class="advertisement-form-row">
							<div class="advertisement-form-group">
								<label class="advertisement-label">Title</label>
								<input v-model.trim="title" class="form-input" placeholder="Type the title" />
								<small v-if="type === AdvertisementType.HTML" class="d-block text-muted mt-1">Only admins see it.</small>
							</div>
							<div v-if="type === AdvertisementType.CARD" class="advertisement-form-group">
								<label class="advertisement-label">Subtitle</label>
								<input v-model.trim="subtitle" class="form-input" placeholder="Type the subtitle" />
							</div>
						</div>
					</section>

					<section v-if="type === AdvertisementType.CARD" class="advertisement-section">
						<div class="advertisement-section-title">Style</div>
						<div class="advertisement-option-cards advertisement-form-group">
							<div v-for="option in styleOptions" :key="option.title" class="advertisement-option-card-container">
								<input :id="`advertisement-style-${option.isOverlay}`" v-model="isOverlay" type="radio" name="advertisement-style" :value="option.isOverlay" />
								<label class="advertisement-option-card advertisement-style-card" :for="`advertisement-style-${option.isOverlay}`">
									<span class="advertisement-style-icon" :class="{ 'advertisement-style-icon-over': option.isOverlay }"></span>
									<p>{{ option.title }}</p>
								</label>
							</div>
						</div>
					</section>

					<section class="advertisement-section">
						<div class="advertisement-section-title">
							Audience
							<small class="advertisement-section-aside form-check form-switch mb-0">
								<input id="advertisement-active" v-model="isActive" class="form-check-input" type="checkbox" role="switch" />
								<label class="form-check-label" for="advertisement-active">Active</label>
							</small>
						</div>
						<div class="advertisement-form-group">
							<div class="d-flex gap-4">
								<div class="form-check form-switch">
									<input id="advertisement-student-dashboard" v-model="showOnStudentDashboard" class="form-check-input" type="checkbox" role="switch" />
									<label class="form-check-label" for="advertisement-student-dashboard">Student Dashboard</label>
								</div>
								<div class="form-check form-switch">
									<input id="advertisement-course-pages" v-model="showOnCoursePages" class="form-check-input" type="checkbox" role="switch" />
									<label class="form-check-label" for="advertisement-course-pages">Course pages</label>
								</div>
							</div>
							<small v-if="visibleErrors.audience" class="d-block text-danger mt-1">{{ visibleErrors.audience }}</small>
						</div>
						<div v-if="showOnCoursePages" class="advertisement-form-group">
							<v-select
								v-model="productVariantIds"
								class="form-input"
								multiple
								:options="courseOptions"
								:reduce="(option) => option.id"
								:closeOnSelect="false"
								label="label"
								placeholder="Search batches by ID or title"
							/>
							<small v-if="visibleErrors.batches" class="d-block text-danger mt-1">{{ visibleErrors.batches }}</small>
						</div>
					</section>

					<section class="advertisement-section">
						<div class="advertisement-section-title">Link</div>
						<div class="advertisement-form-row">
							<div class="advertisement-form-group">
								<label class="advertisement-label">Goes to</label>
								<select v-model="linkType" class="form-input">
									<option :value="LinkType.NONE">No link</option>
									<option :value="LinkType.COURSE">A course</option>
									<option :value="LinkType.CUSTOM">Custom link</option>
								</select>
							</div>
							<div v-if="linkType === LinkType.COURSE" class="advertisement-form-group">
								<label class="advertisement-label">Course</label>
								<v-select
									v-model="linkedCourseId"
									class="form-input"
									:options="linkableCourseOptions"
									:reduce="(option) => option.id"
									label="label"
									placeholder="Search by ID or title"
								/>
								<small v-if="visibleErrors.link" class="d-block text-danger mt-1">{{ visibleErrors.link }}</small>
							</div>
							<div v-if="linkType === LinkType.CUSTOM" class="advertisement-form-group">
								<label class="advertisement-label">URL</label>
								<input v-model.trim="customLink" class="form-input" placeholder="/search or https://..." />
								<small v-if="visibleErrors.link" class="d-block text-danger mt-1">{{ visibleErrors.link }}</small>
							</div>
						</div>
						<div v-if="hasButtonOption" class="advertisement-form-row">
							<div class="advertisement-form-group">
								<label class="advertisement-label">Click</label>
								<div class="form-check form-switch">
									<input id="advertisement-has-button" v-model="hasButton" class="form-check-input" type="checkbox" role="switch" />
									<label class="form-check-label" for="advertisement-has-button">Show a button</label>
								</div>
							</div>
							<div class="advertisement-form-group">
								<template v-if="hasButton">
									<label class="advertisement-label">Button text</label>
									<input v-model.trim="buttonText" class="form-input" placeholder="Type the button text" />
									<small v-if="visibleErrors.buttonText" class="d-block text-danger mt-1">{{ visibleErrors.buttonText }}</small>
								</template>
								<small v-else class="d-block text-muted">The whole card is clickable.</small>
							</div>
						</div>
					</section>
				</div>

				<!-- Right: previews -->
				<div class="advertisement-modal-previews">
					<label class="advertisement-label">Standard</label>
					<small class="advertisement-preview-caption">Student Dashboard and course pages</small>
					<div class="advertisement-preview-stage">
						<AdvertisementBanner v-if="hasPreview" :advertisement="draftSlide" :height="settings.height" preview />
						<p v-else class="advertisement-preview-empty">{{ emptyPreviewText }}</p>
					</div>

					<label class="advertisement-label">Compact</label>
					<small class="advertisement-preview-caption">Ebook course sidebar</small>
					<div class="advertisement-preview-stage compact">
						<AdvertisementBanner v-if="hasPreview" :advertisement="draftSlide" :height="settings.compactHeight" compact preview />
						<p v-else class="advertisement-preview-empty">{{ emptyPreviewText }}</p>
					</div>
				</div>
			</div>

			<div class="advertisement-modal-footer">
				<p v-if="Object.keys(visibleErrors).length > 0" class="text-danger small me-auto mb-0">Fix the fields marked in red.</p>
				<button type="button" class="advertisement-modal-button secondary" @click="$emit('close')">Cancel</button>
				<button type="button" class="advertisement-modal-button" :disabled="isUploading" @click="save">Save</button>
			</div>
		</div>
	</b-modal>
</template>

<script>
import vue2Dropzone from "vue2-dropzone";
import { mapActions, mapGetters } from "vuex";
import AdvertisementBanner from "../../../Advertisement/AdvertisementBanner.vue";
import { ProductVariantStatus } from "../../../../constants/productVariant";
import { AdvertisementType, LinkType } from "../../../../constants/advertisement";

const COURSE_LINK_PREFIX = "/product/details/";

export default {
	name: "AddEditAdvertisementModal",
	components: { vueDropzone: vue2Dropzone, AdvertisementBanner },
	props: {
		visible: { type: Boolean, default: false },
		advertisement: { type: Object, default: null },
		settings: { type: Object, default: () => ({}) },
		courseOptions: { type: Array, default: () => [] },
	},
	data() {
		return {
			typeOptions: [
				{ value: AdvertisementType.CARD, title: "Card", description: "Image, title, subtitle and an optional button." },
				{ value: AdvertisementType.HTML, title: "HTML", description: "Your own markup: badges, panels, embeds." },
			],
			styleOptions: [
				{ isOverlay: false, title: "Text below image" },
				{ isOverlay: true, title: "Text over image" },
			],
			showErrors: false,
			AdvertisementType,
			LinkType,
			type: AdvertisementType.CARD,
			image: "",
			imageError: "",
			title: "",
			subtitle: "",
			isOverlay: false,
			hasButton: false,
			buttonText: "",
			html: "",
			linkType: LinkType.NONE,
			linkedCourseId: null,
			customLink: "",
			showOnCoursePages: false,
			productVariantIds: [],
			isActive: true,
			showOnStudentDashboard: true,
			isUploading: false,
			dropzoneOptions: {
				url: " ",
				autoProcessQueue: false,
				maxFiles: 1,
				acceptedFiles: "image/*",
				previewTemplate: "<div class='d-none'></div>",
			},
		};
	},
	computed: {
		...mapGetters("appState", ["isAdvertisementHtmlEnabled"]),
		// Same rule as the student screens: everything except Draft
		linkableCourseOptions() {
			return this.courseOptions.filter((option) => option.status !== ProductVariantStatus.DRAFT.toLowerCase());
		},
		link() {
			if (this.linkType === LinkType.COURSE) {
				return this.linkedCourseId ? `${COURSE_LINK_PREFIX}${this.linkedCourseId}` : "";
			}

			return this.linkType === LinkType.CUSTOM ? this.customLink : "";
		},
		hasPreview() {
			return this.type === AdvertisementType.HTML ? Boolean(this.html.trim()) : Boolean(this.image || this.title || this.subtitle);
		},
		emptyPreviewText() {
			return this.type === AdvertisementType.HTML ? "Add the HTML to see the preview." : "Add an image or title to see the preview.";
		},
		hasButtonOption() {
			return this.type === AdvertisementType.CARD && this.linkType !== LinkType.NONE;
		},
		draftSlide() {
			const slide = { isActive: this.isActive, showOnStudentDashboard: this.showOnStudentDashboard };
			if (this.title) {
				slide.title = this.title;
			}
			if (this.type === AdvertisementType.HTML) {
				slide.html = this.html;
			} else {
				slide.image = this.image;
				if (this.isOverlay) {
					slide.overlay = true;
				}
				if (this.subtitle) {
					slide.subtitle = this.subtitle;
				}
				if (this.hasButton && this.buttonText && this.link) {
					slide.buttonText = this.buttonText;
				}
			}
			if (this.link) {
				slide.link = this.link;
			}
			if (this.showOnCoursePages && this.productVariantIds.length > 0) {
				slide.productVariantIds = this.productVariantIds;
			}

			return slide;
		},
		// Keyed by field so each message shows under its own field
		errors() {
			const errors = {};
			if (this.type === AdvertisementType.CARD && !this.image) {
				errors.image = "Add an image.";
			}
			if (this.type === AdvertisementType.HTML && !this.html.trim()) {
				errors.html = "Add the HTML.";
			}
			if (!this.showOnStudentDashboard && !this.showOnCoursePages) {
				errors.audience = "Show it on the Student Dashboard or course pages.";
			}
			if (this.showOnCoursePages && this.productVariantIds.length === 0) {
				errors.batches = "Pick at least one batch.";
			}
			if (this.linkType === LinkType.COURSE && !this.linkedCourseId) {
				errors.link = "Pick a course to link to.";
			}
			// A site path (not "//") or a full http(s) address
			if (this.linkType === LinkType.CUSTOM && !/^(\/(?!\/)|https?:\/\/)/i.test(this.customLink)) {
				errors.link = "Enter a path starting with / or an address starting with https://.";
			}
			if (this.hasButtonOption && this.hasButton && !this.buttonText) {
				errors.buttonText = "Add the button text.";
			}

			return errors;
		},
		visibleErrors() {
			return this.showErrors ? this.errors : {};
		},
	},
	watch: {
		visible(isVisible) {
			if (isVisible) {
				this.loadAdvertisement(this.advertisement || {});
			}
		},
	},
	methods: {
		...mapActions("content", ["uploadContentFile"]),
		loadAdvertisement(slide) {
			const link = String(slide.link || "");
			const isCourseLink = link.toLowerCase().startsWith(COURSE_LINK_PREFIX);

			this.showErrors = false;
			this.imageError = "";
			this.type = slide.html ? AdvertisementType.HTML : AdvertisementType.CARD;
			this.image = slide.image || "";
			this.title = slide.title || "";
			this.subtitle = slide.subtitle || "";
			this.isOverlay = String(slide.overlay).toLowerCase() === "true";
			this.buttonText = slide.buttonText || "";
			this.hasButton = Boolean(slide.buttonText);
			this.html = slide.html || "";
			this.linkType = link ? (isCourseLink ? LinkType.COURSE : LinkType.CUSTOM) : LinkType.NONE;
			this.linkedCourseId = isCourseLink ? link.slice(COURSE_LINK_PREFIX.length) : null;
			this.customLink = isCourseLink ? "" : link;
			this.productVariantIds = [].concat(slide.productVariantIds || []).map(String);
			this.showOnCoursePages = this.productVariantIds.length > 0;
			this.isActive = slide.isActive !== false;
			this.showOnStudentDashboard = slide.showOnStudentDashboard !== false;
		},
		save() {
			this.showErrors = true;
			if (Object.keys(this.errors).length === 0) {
				this.$emit("save", this.draftSlide);
			}
		},
		async uploadImage(file) {
			this.$nextTick(() => this.$refs.dropzone?.removeFile(file));

			if (this.isUploading) {
				return;
			}
			// Dropzone reports the file before checking its type
			if (!file.type.startsWith("image/")) {
				this.imageError = "Choose an image file (JPG, PNG, GIF or WebP).";
				return;
			}

			const formData = new FormData();
			formData.append("file", file);
			formData.append("entity", "Banner");
			formData.append("IsCompressionRequired", true);
			formData.append("type", "Image");

			this.imageError = "";
			this.isUploading = true;
			try {
				const url = await this.uploadContentFile(formData);
				this.image = url || "";
				this.imageError = url ? "" : "The image could not be uploaded. Try again.";
			} catch {
				this.imageError = "The image could not be uploaded. Try again.";
			} finally {
				this.isUploading = false;
			}
		},
		onVisibilityChange(isVisible) {
			if (!isVisible) {
				this.$emit("close");
			}
		},
	},
};
</script>

<style>
.advertisement-modal .modal-body {
	display: flex;
	padding: 0 !important;
	overflow: hidden;
}
</style>

<style scoped>
/* Layout */
.advertisement-modal-body {
	display: flex;
	flex-direction: column;
	width: 100%;
}
.advertisement-modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 2rem 2rem 1.5rem;
}
.advertisement-modal-title {
	margin: 0;
	font-size: 2rem;
	font-weight: 450;
	line-height: 1.5;
	color: #000000;
}
.advertisement-modal-close {
	border: 0;
	background: transparent;
}
.advertisement-modal-close img {
	height: 1.5rem;
}
.advertisement-modal-columns {
	display: flex;
	gap: 2rem;
	height: 65vh;
	padding: 0 2rem;
}
.advertisement-modal-form {
	flex: 1;
	min-width: 0;
	padding-right: 0.5rem;
	overflow-y: auto;
	counter-reset: section;
}
.advertisement-modal-previews {
	flex-shrink: 0;
	align-self: flex-start;
	width: 22rem;
	max-height: 100%;
	padding: 1rem;
	border: 1px solid #e6e6e6;
	border-radius: 8px;
	overflow-y: auto;
}

/* Sections */
.advertisement-section {
	margin-bottom: 1rem;
	padding: 1.1rem 1.25rem 0;
	border: 1px solid #e6e6e6;
	border-radius: 8px;
	counter-increment: section;
}
.advertisement-section-title {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
	font-size: 0.95rem;
	font-weight: 600;
	color: #231f20;
}
.advertisement-section-title::before {
	content: counter(section);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.5rem;
	height: 1.5rem;
	border-radius: 50%;
	background-color: var(--primary-color);
	color: #ffffff;
	font-size: 0.75rem;
}
.advertisement-section-aside {
	margin-left: auto;
	color: #838383;
	font-size: 0.8125rem;
	font-weight: 400;
}

/* Fields */
.advertisement-label {
	display: block;
	margin-bottom: 0.4rem;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: #727272;
}
.advertisement-form-group {
	margin-bottom: 1.25rem;
}
.advertisement-form-row {
	display: flex;
	gap: 1.5rem;
}
.advertisement-form-row > * {
	flex: 1;
	min-width: 0;
}
.advertisement-modal-form .form-input {
	width: 100%;
	padding: 0.5rem 0;
	border: 0;
	border-bottom: 1.5px solid #b2b2b2;
	border-radius: 0;
	outline: none;
	box-shadow: none;
	background-color: transparent;
	font-size: 0.9375rem;
}
.advertisement-modal-form input.form-input,
.advertisement-modal-form select.form-input {
	height: 2.5rem;
}
.advertisement-modal-form select.form-input {
	padding: 0 1.5rem 0 0;
	-webkit-appearance: none;
	appearance: none;
	background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='10'%3E%3Cpath fill='%23727272' d='M7 10 0 2.5 1.5 1 7 6.9 12.5 1 14 2.5Z'/%3E%3C/svg%3E") no-repeat right 0.4rem center;
}
.advertisement-modal-form .form-input::placeholder,
.advertisement-modal-form .v-select.form-input ::v-deep .vs__search::placeholder {
	color: #9e9e9e;
}
.advertisement-modal-form .v-select.form-input {
	padding: 0;
}
.advertisement-modal-form .v-select.form-input ::v-deep .vs__dropdown-toggle {
	align-items: center;
	min-height: calc(2.5rem - 1.5px);
	padding: 0;
	border: 0;
}
.advertisement-modal-form .v-select.form-input ::v-deep .vs__selected-options {
	align-items: center;
	padding: 0;
}
.advertisement-modal-form .v-select.form-input ::v-deep .vs__selected,
.advertisement-modal-form .v-select.form-input ::v-deep .vs__search {
	font-size: 0.9375rem;
}
.advertisement-modal-form .v-select.form-input ::v-deep .vs__search {
	margin-left: 0;
	padding-left: 0;
}
.advertisement-modal-form .v-select.form-input ::v-deep .vs__dropdown-menu {
	top: calc(100% + 0.25rem);
	border-top-style: solid;
	border-radius: 4px;
}
.advertisement-modal-form .v-select.vs--single.form-input ::v-deep .vs__selected-options {
	flex-wrap: nowrap;
	min-width: 0;
}
.advertisement-modal-form .v-select.vs--single.form-input ::v-deep .vs__selected {
	display: block;
	margin-left: 0;
	padding-left: 0;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.advertisement-modal-form .advertisement-form-row .form-check {
	padding-top: 0.5rem;
}
.advertisement-modal-form .form-check-label {
	font-size: 0.9375rem;
	font-weight: 400;
}
.advertisement-modal-form .advertisement-section-aside .form-check-label {
	font-size: inherit;
}
.advertisement-modal-form .form-textarea {
	width: 100%;
	padding: 0.5rem;
	border: 1.5px solid #b2b2b2;
	border-radius: 6px;
	font-family: monospace;
	font-size: 0.8125rem;
}

/* Option cards */
.advertisement-option-cards {
	display: flex;
	flex-wrap: wrap;
	gap: 1.5rem;
}
.advertisement-option-card-container input[type="radio"] {
	position: fixed;
	width: 0;
	opacity: 0;
}
.advertisement-option-card {
	position: relative;
	display: block;
	max-width: 13.75rem;
	height: 100%;
	padding: 1.125rem;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
	box-shadow: 2px 3px 5px rgba(197, 197, 197, 0.15);
	background-color: #ffffff;
	color: #727272;
	font-size: 1rem;
	line-height: 1.37;
	cursor: pointer;
}
.advertisement-option-card p {
	margin-bottom: 0.5rem;
	color: #000000;
	font-weight: 500;
}
.advertisement-option-card-container input[type="radio"]:checked + .advertisement-option-card {
	border-color: var(--primary-color);
}
.advertisement-option-card-container input[type="radio"]:checked + .advertisement-option-card::after {
	content: "";
	position: absolute;
	top: -0.5rem;
	right: -0.5rem;
	width: 1.625rem;
	height: 1.625rem;
	border-radius: 50%;
	background: var(--primary-color) url("~@/assets/images/check-icon.svg") no-repeat center / 0.8rem auto;
}

/* Style icons */
.advertisement-modal-form .advertisement-style-card {
	width: 10rem;
	padding: 0.9rem;
}
.advertisement-modal-form .advertisement-style-card p {
	margin: 0.6rem 0 0;
	font-size: 0.875rem;
}
.advertisement-modal-form .advertisement-style-icon {
	display: block;
	height: 4.5rem;
	background: url("~@/assets/images/advertisement-style-below.svg") no-repeat center / contain;
}
.advertisement-modal-form .advertisement-style-icon-over {
	background-image: url("~@/assets/images/advertisement-style-over.svg");
}

/* Image */
.advertisement-dropzone {
	padding: 2rem;
	border: 2px dashed #dcdcdc;
	border-radius: 6px;
	background-color: #fafafa;
	text-align: center;
	cursor: pointer;
}
.advertisement-dropzone-title {
	margin: 0;
	font-weight: 500;
	color: #231f20;
}
.advertisement-dropzone-subtitle {
	margin: 0.25rem 0 0;
	font-size: 0.8125rem;
	color: #838383;
}
.advertisement-image-preview {
	position: relative;
	width: fit-content;
}
.advertisement-image-preview img {
	width: 16rem;
	height: 8rem;
	object-fit: cover;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
}
.advertisement-image-remove {
	position: absolute;
	top: 0.5rem;
	right: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	padding: 0;
	background: #ffffff;
	border: 0.8px solid #ff0000;
	box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.07);
	border-radius: 50%;
}
.advertisement-image-preview .advertisement-image-remove img {
	width: 0.75rem;
	height: 0.75rem;
	border: 0;
}

/* Previews */
.advertisement-preview-caption {
	display: block;
	margin: -0.25rem 0 0.75rem;
	color: #838383;
}
.advertisement-preview-stage {
	margin-bottom: 1.25rem;
	border-radius: 6px;
	background-color: #f5f5f5;
}
.advertisement-preview-stage:last-child {
	margin-bottom: 0;
}
.advertisement-preview-stage.compact {
	display: flex;
	justify-content: center;
}
.advertisement-preview-stage.compact > * {
	width: 274px;
}
.advertisement-preview-empty {
	margin: 0;
	padding: 2.5rem 0;
	color: #838383;
	font-size: 0.8125rem;
	text-align: center;
}

/* Footer */
.advertisement-modal-footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 1rem;
	padding: 1.5rem 2rem 2rem;
}
.advertisement-modal-button {
	padding: 0.875rem 1.5rem;
	border: 1px solid var(--primary-color);
	border-radius: 6px;
	background: #ffffff;
	filter: drop-shadow(3px 5px 6px rgba(197, 197, 197, 0.3));
	color: #000000;
	font-size: 0.875rem;
	line-height: 1;
	text-transform: uppercase;
}
.advertisement-modal-button.secondary {
	border: 1px solid #e5e7eb;
	color: #374151;
}
.advertisement-modal-button:hover:not(:disabled) {
	border-color: #ffffff;
	background-color: var(--primary-color);
	color: #ffffff;
}
.advertisement-modal-button.secondary:hover {
	border-color: #e5e7eb;
	background-color: #f3f4f6;
	color: #374151;
}
.advertisement-modal-button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
@media (max-width: 992px) {
	.advertisement-modal-columns {
		flex-direction: column;
		height: auto;
		max-height: 70vh;
		overflow-y: auto;
	}
	.advertisement-modal-form,
	.advertisement-modal-previews {
		width: 100%;
		max-height: none;
		overflow: visible;
	}
	.advertisement-form-row {
		flex-direction: column;
		gap: 0;
	}
}
</style>
